<template>
  <CModal
    v-model:visible="showModal"
    title="Select a Class"
    size="xl"
    @close="closeModal"
  >
    <CCard>
      <CCardHeader class="d-flex justify-content-end">
        <CInput
          v-model="filters.search"
          class="mb-0"
          placeholder="Search"
          @input="fetchData"
        />
        <CButton
          class="ml-3"
          color="primary"
          @click="openFilters"
        >
          Filters
        </CButton>
      </CCardHeader>

      <Transition>
        <CCardHeader
          v-if="activeFiltersList.length > 0"
          class="active-filters-options"
        >
          <div
            v-for="filter in activeFiltersList"
            :key="filter.title"
          >
            <strong class="mr-2">{{ filter.title }}:</strong>
            <span
              v-for="option in filter.options"
              :key="`${filter.slug}${option.title}`"
              class="badge bg-info text-white mr-2 h6 c-pointer"
              @click="removeActiveFilter(filter.slug, option.value)"
            >
              {{ option.title }} <i class="fas fa-times" />
            </span>
          </div>
        </CCardHeader>
      </Transition>

      <CCardBody>
        <CDataTable
          :items="data"
          :fields="fields"
          :pagination="false"
          :sorter="{external: true}"
          :loading="loading"
          class="mb-0 table-hover"
          @update:sorter-value="sortingChange"
          @row-clicked="onRowClicked"
        >
          <template #name="data">
            <td>
              <span
                v-c-tooltip="{
                  content: statuses[data.item.status],
                  placement: 'left',
                }"
              >
                <span
                  class="status-badge mr-1"
                  :class="{
                    'bg-success': data.item.status === 'released',
                    'bg-secondary': data.item.status === 'draft',
                    'bg-dark': data.item.status === 'ready',
                    'bg-info': data.item.status === 'filmed',
                  }"
                />{{ data.item.name }}
              </span>
            </td>
          </template>
          <template #categories="data">
            <td>
              <span
                v-for="cat in data.item.categories"
                class="badge bg-info text-white mr-1"
              >{{ cat }}</span>
            </td>
          </template>
          <template #instructors="data">
            <td>
              {{ data.item.instructors.map(i => i.name).join(', ') }}
            </td>
          </template>
          <template #release_date="data">
            <td>
              {{ data.item.release_date ? $formatDateTime(data.item.release_date) : '' }}
            </td>
          </template>
          <template #production_date="data">
            <td>
              {{ data.item.production_date ? $formatDateTime(data.item.production_date) : '' }}
            </td>
          </template>
        </CDataTable>
        <pagination
          :current-page="options.page"
          :last-page="options.last_page"
          class="mr-3"
          :loading="loading"
          @paginate="pageChange"
        />
      </CCardBody>
    </CCard>

    <filters-modal
      ref="filters_modal"
      :show-status="false"
      :show-length="true"
      @apply="changeFilters"
    />

    <template #footer>
      <CButton
        color="secondary"
        @click="closeModal"
      >
        Cancel
      </CButton>
    </template>
  </CModal>
</template>

<script>
import externalTable from '@/mixins/externalTable'
import FiltersModal from '@/views/sessions/FiltersModal'
import { useAppStore } from '@/store'

export default {
  name: 'SessionPickerModal',
  components: { FiltersModal },
  mixins: [externalTable],
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const appStore = useAppStore()
    return { appStore }
  },
  data() {
    return {
      showModal: false,
      filters: {
        search: '',
        instructor: [],
        class_type: [],
        language: [],
        tag: [],
        category: [],
        length: [],
        status: null,
      },
      statuses: {
        released: 'Released',
        draft: 'Draft',
        ready: 'Ready',
        filmed: 'Filmed',
      },
      fields: [
        { key: 'name', label: 'Name' },
        { key: 'categories', label: 'Categories', sorter: false },
        { key: 'instructors', label: 'Instructors', sorter: false },
        { key: 'length', label: 'Length' },
        { key: 'class_type', label: 'Type' },
        { key: 'language', label: 'Language' },
        { key: 'production_date', label: 'Production Date' },
        { key: 'release_date', label: 'Release Date' },
        { key: 'length', label: 'Length' },
      ],
      url: '/sessions'
    }
  },
  computed: {
    filtersData() {
      return this.appStore.classesFilterData || {}
    },
    activeFiltersList() {
      let filters = []
      if (!this.filtersData) return filters

      let getFilterOptionNameById = (optionName, id) => {
        let option = (this.filtersData[optionName] || []).find(o => o.id === id)
        return option ? option.name : ''
      }

      if (this.filters.status) {
        filters.push({
          title: 'Status',
          slug: 'status',
          options: [{title: this.statuses[this.filters.status], value: this.filters.status}]
        })
      }

      [
        { title: 'Instructors', slug: 'instructor', filterField: 'instructors' },
        { title: 'Languages', slug: 'language', filterField: 'languages' },
        { title: 'Class types', slug: 'class_type', filterField: 'class_types' },
        { title: 'Categories', slug: 'category', filterField: 'categories' },
        { title: 'Tags', slug: 'tag', filterField: 'tags' },
        { title: 'Length', slug: 'length', filterField: 'length' },
      ].forEach(({ title, slug, filterField }) => {
        if (this.filters[slug] && this.filters[slug].length) {
          filters.push({
            title,
            slug,
            options: this.filters[slug].map(valueId => ({
              value: valueId,
              title: getFilterOptionNameById(filterField, valueId)
            })),
          })
        }
      })

      return filters
    }
  },
  watch: {
    show(newVal) {
      this.showModal = newVal
      if (newVal) {
        this.fetchData()
      }
    },
    showModal(newVal) {
      if (!newVal) {
        this.$emit('update:show', false)
      }
    }
  },
  methods: {
    closeModal() {
      this.showModal = false
      this.$emit('update:show', false)
    },
    openFilters() {
      this.$refs.filters_modal?.open(this.filters)
    },
    changeFilters(data) {
      this.options.page = 1
      this.filters = {...this.filters, ...data}
      this.fetchData()
    },
    onRowClicked(item) {
      this.$emit('selected', item)
      this.closeModal()
    },
    removeActiveFilter(filterFieldName, optionValue) {
      this.filters[filterFieldName] = Array.isArray(this.filters[filterFieldName])
        ? this.filters[filterFieldName].filter(value => value !== optionValue)
        : null
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.active-filters-options{
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
}
.active-filters-options > div {
  line-height: 2.2rem;
  margin-right: .7rem;
}

:deep(.table-hover tbody tr) {
  cursor: pointer;
}
:deep(.table-hover tbody tr:hover) {
  background-color: rgba(0, 0, 0, 0.075);
}
</style>
